import { useState } from "react";
import { apiFetch } from "@/lib/api-client";

export interface UploadedDocumentSummary {
  issuerTaxId: string;
  issuerName: string;
  documentTypeCode: string;
  folio: number;
  issueDate: string;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  lineItemsCount: number;
}

export interface UploadResult {
  success: boolean;
  documentsStored: number;
  duplicatesSkipped: number;
  documents: Array<{
    documentId?: string;
    status: "stored" | "duplicate" | "error";
    summary: UploadedDocumentSummary;
    error?: string;
  }>;
  error?: string;
}

export function useUploadDocument() {
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File): Promise<UploadResult> => {
    setIsUploading(true);
    setError(null);
    setResult(null);

    try {
      // Read file content as text
      const content = await file.text();

      // Send to API
      const response = await apiFetch.post<UploadResult>(
        "/api/received-documents/upload",
        {
          xml: content,
          isBase64: false,
        },
      );

      setResult(response);
      return response;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Error al subir el archivo";
      setError(message);
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  const reset = () => {
    setIsUploading(false);
    setResult(null);
    setError(null);
  };

  return {
    upload,
    isUploading,
    result,
    error,
    reset,
  };
}
